import {
  OrderExecutionTermsOperationKind,
  OrderExecutionTermsOrderType,
  OrderMarketType,
  OrderTransactionVenueType,
} from 'api/types/entity/account/order';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { Input } from 'libs/ui';
import { MAX_FLOATING_POINTS } from 'modules/account/orderModule/common/constants';

import { Field, useFormContext } from '../../../forms/orderForm';
import { OrderSection } from '../../OrderSection';

interface Props {
  isShowOrderType: boolean;
  isShowOperationKind: boolean;
  isShowYield: boolean;
  isShowOperationPurpose?: boolean;

  marketType?: {
    show: boolean;
    value?: OrderMarketType;
  };

  transactionVenue?: {
    show: boolean;
    value?: OrderTransactionVenueType;
  };
}

export const ExecutionTermsSection = ({
  isShowOperationKind,
  isShowOrderType,
  isShowYield,
  isShowOperationPurpose,
  marketType,
  transactionVenue,
}: Props) => {
  const { t } = useTranslation();

  const { readOnly } = useFormContext();

  return (
    <OrderSection
      fields={lodash.compact([
        isShowOrderType && (
          <Field.Autocomplete
            fullWidth
            showRequiredMark
            label={t(
              'account.order.sections.executionTermsSection.fields.orderType.label'
            )}
            name="executionTerms.orderType"
            options={Object.values(OrderExecutionTermsOrderType).map(
              (type) => ({
                label: t(
                  `account.order.sections.executionTermsSection.fields.orderType.types.${type}`
                ),
                value: type,
              })
            )}
            placeholder={t(
              'account.order.sections.executionTermsSection.fields.orderType.placeholder'
            )}
            variant="select"
          />
        ),
        isShowOperationKind && (
          <Field.Autocomplete
            fullWidth
            showRequiredMark
            label={t(
              'account.order.sections.executionTermsSection.fields.operationKind.label'
            )}
            name="executionTerms.operationKind"
            options={Object.values(OrderExecutionTermsOperationKind).map(
              (type) => ({
                label: t(
                  `account.order.sections.executionTermsSection.fields.operationKind.types.${type}`
                ),
                value: type,
              })
            )}
            placeholder={t(
              'account.order.sections.executionTermsSection.fields.operationKind.placeholder'
            )}
            variant="select"
          />
        ),
        isShowYield && (
          <Field.Decimal
            fullWidth
            label={t(
              'account.order.sections.executionTermsSection.fields.yield.label'
            )}
            maxFloatingPoints={MAX_FLOATING_POINTS}
            name="executionTerms.yield"
            placeholder={t(
              'account.order.sections.executionTermsSection.fields.yield.placeholder'
            )}
          />
        ),
        isShowOperationPurpose && (
          <Field.Text
            fullWidth
            label={t(
              'account.order.sections.executionTermsSection.fields.operationPurpose.label'
            )}
            name="executionTerms.operationPurpose"
            placeholder={t(
              'account.order.sections.executionTermsSection.fields.operationPurpose.placeholder'
            )}
          />
        ),
        <Field.Text
          fullWidth
          label={t(
            'account.order.sections.executionTermsSection.fields.additionalNotes.label'
          )}
          name="executionTerms.additionalNotes"
          placeholder={t(
            'account.order.sections.executionTermsSection.fields.additionalNotes.placeholder'
          )}
        />,
        marketType?.show && (
          <Input
            fullWidth
            readOnly
            disabled={!readOnly}
            label={t(
              'account.order.sections.executionTermsSection.fields.marketType.label'
            )}
            value={
              marketType.value &&
              t(
                `account.order.sections.executionTermsSection.fields.marketType.types.${marketType.value}`
              )
            }
          />
        ),
        transactionVenue?.show && (
          <Input
            fullWidth
            readOnly
            disabled={!readOnly}
            label={t(
              'account.order.sections.executionTermsSection.fields.transactionVenue.label'
            )}
            value={
              transactionVenue.value &&
              t(
                `account.order.sections.executionTermsSection.fields.transactionVenue.types.${transactionVenue.value}`
              )
            }
          />
        ),
      ])}
      title={t('account.order.sections.executionTermsSection.title')}
    />
  );
};
